/**
 * コンポーネント、サービスで共通して持つ親クラス
 *
 */
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription, forkJoin, Subject } from 'rxjs';

/**
 * SupportClass、サービスで共通して持つ親クラス
 * 必要なクラスでextendsして使用する
 */
@Injectable({
  providedIn: 'root',
})
export abstract class ServicingBaseClass implements OnDestroy {
  // Subscribe するためのプロパティ
  private _subscriptions: Subscription[] = [];

  constructor() {}

  // コンポーネント消滅時、
  ngOnDestroy(): void {
    this._subscriptions
      .filter((subscription) => subscription != null)
      .forEach((subscription) => subscription.unsubscribe());
    this.destroy();
  }

  /**
   * ngOnDestroy時に呼ばれる処理、継承先のコンポーネントで使用するngOnDestoroyの代替
   */
  abstract destroy(): void;

  /**
   * Subscriptionをコンポーネントで持たせないようにするための関数
   *
   * @param observable observable処理
   * @param nextEvent 処理内容
   * @param errorEvent エラー時処理内容
   * @param completeEvent 完了時処理内容
   *
   * 使用例 this.subscribeService(this.sampleFecadeFacade.getAll(),state =>{
   *    console.log(state)
   *    if(state && state[0]){
   *      this.fecadeValue=state[0].str;
   *      this.fecadeValue2=state[0].num;
   *    }
   *  })
   *
   * takeを使用したい場合などは以下のように指定すればオペレーターも使用可能
   * this.subscribeService(this.sampleFecadeFacade.getAll().pipe(take(1)),state =>
   *
   */
  protected subscribeService<T, V>(
    observable$: Observable<T>,
    nextEvent: (value: T) => void,
    errorEvent?: (value: V) => void,
    completeEvent?: () => void
  ): void {
    this._subscriptions.push(observable$.subscribe({ next: nextEvent, error: errorEvent, complete: completeEvent }));
  }

  /**
   *
   * forkJoin用
   *
   * @param observables  Observable配列
   * @param nextEvent 処理内容
   * @param errorEvent エラー時処理内容
   * @param completeEvent 完了時処理内容
   *
   * 使用例
   * this.forkJoinService(
   *  [
   *    getResponse(this.airportListService.loadAll()),
   *    getResponse(this.regionListService.loadAll())
   *  ],
   *  ([airportState,regionState]) =>{
   *    this.airportListParts.airports=airportState.data;
   *    this.airportListParts2.airports=airportState.data;
   *    this.airportListParts.regions=regionState.data;
   *    this.airportListParts2.regions=regionState.data;
   *  }
   * )
   *
   */
  protected forkJoinService<V>(
    observables: Observable<any>[],
    nextEvent: (value: any[]) => void,
    errorEvent?: (value: V) => void,
    completeEvent?: () => void
  ): void {
    this._subscriptions.push(
      forkJoin(observables).subscribe({ next: nextEvent, error: errorEvent, complete: completeEvent })
    );
  }
}
